"use client";

import * as React from "react";

import { Button } from "@unreserved/ui/components/button";
import { Calendar } from "@unreserved/ui/components/calender";
import { Label } from "@unreserved/ui/components/label";
import { Popover, PopoverContent, PopoverTrigger } from "@unreserved/ui/components/popover";
import { cn } from "@unreserved/utils/cn";
import { type VariantProps, cva } from "class-variance-authority";

import { CalenderIcon } from "@/app/_components/icons/calender";
import { InfoWarningIcon } from "@/app/_components/icons/info-warning";

const dateInputVariants = cva(
  "flex w-full justify-between rounded-md border border-border-400 bg-white! p-3 text-sm text-typography-950! transition-all placeholder:text-typography-600! focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 ring-0 focus-visible:ring-4 h-auto shadow-none font-normal",
  {
    variants: {
      variant: {
        default:
          "border-border-400 focus-visible:border-primary-500 focus-visible:ring-primary-200",
        error: "border-error-700 focus-visible:border-error-700 focus-visible:ring-error-200",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export interface DateInputProps extends VariantProps<typeof dateInputVariants> {
  label?: React.ReactNode;
  error?: string;
  placeholder?: string;
  disabled?: boolean;
  value?: Date;
  onChange?: (date: Date | undefined) => void;
  className?: string;
  id?: string;
  disablePastDates?: boolean;
  name?: string;
}

function formatDateToDDMMYYYY(date: Date): string {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = String(date.getFullYear());
  return `${day}/${month}/${year}`;
}

const DateInput = React.forwardRef<HTMLButtonElement, DateInputProps>(
  (
    {
      className,
      variant,
      label,
      error,
      placeholder = "Select date",
      disabled,
      value,
      onChange,
      id,
      disablePastDates = false,
      name,
      ...props
    },
    ref,
  ) => {
    const [open, setOpen] = React.useState(false);
    const [date, setDate] = React.useState<Date | undefined>(value);

    React.useEffect(() => {
      setDate(value);
    }, [value]);

    const handleSelect = (selectedDate: Date | undefined) => {
      setDate(selectedDate);
      onChange?.(selectedDate);
      setOpen(false);
    };

    const computedVariant = error ? "error" : variant;

    return (
      <div className="w-full">
        {label && (
          <Label
            htmlFor={id || name}
            className="text-typography-900 mb-2 block rounded-md text-sm font-medium md:rounded-lg">
            {label}
          </Label>
        )}
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              ref={ref}
              id={id}
              disabled={disabled}
              className={cn(dateInputVariants({ variant: computedVariant }), className)}
              aria-invalid={!!error}
              aria-describedby={error ? `${id || name}-error` : undefined}
              {...props}>
              <span className={cn(!date && "text-typography-600")}>
                {date ? formatDateToDDMMYYYY(date) : placeholder}
              </span>

              <CalenderIcon width={18} height={18} className="ml-2" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto overflow-hidden p-0" align="start">
            <Calendar
              mode="single"
              selected={date}
              onSelect={handleSelect}
              captionLayout="dropdown"
              autoFocus
              classNames={{
                root: "!bg-white text-black",
                month: "!bg-white",
                table: "!bg-white",
              }}
              startMonth={new Date()}
              endMonth={new Date(new Date().getFullYear() + 1, 11)}
              disabled={
                disabled
                  ? true
                  : disablePastDates
                    ? (date: Date) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const compareDate = new Date(date);
                        compareDate.setHours(0, 0, 0, 0);
                        return compareDate < today;
                      }
                    : false
              }
            />
          </PopoverContent>
        </Popover>
        {error && (
          <div className="mt-1 flex flex-row items-center gap-1">
            <InfoWarningIcon width={13} height={13} />
            <p id={`${id || name}-error`} className="text-error-600 flex-row text-sm" role="alert">
              {error}
            </p>
          </div>
        )}
      </div>
    );
  },
);

DateInput.displayName = "DateInput";

export { DateInput, dateInputVariants };
